import {DEFAULT_BOARD_SIZE} from 'ghostban';
import {useEffect, useState} from 'react';

declare global {
    interface Window {
        FS: any;
        Module: any;
        katagoStatusHandler: (status: number) => void;
    }
}

class Input {
    public buffer: string;
    private resolve: (() => void) | null | undefined;
    private reject: (() => void) | null | undefined;
    private onCommandSubmit?: (command: string) => void;

    constructor(onCommandSubmit?: (command: string) => void) {
        this.buffer = '';
        this.onCommandSubmit = onCommandSubmit;
    }

    send(data: string): void {
        // this.buffer += data + '\n';
        this.buffer = data + '\n';
        if (this.onCommandSubmit) this.onCommandSubmit(this.buffer);
        if (this.resolve) {
            this.resolve();
        }
    }

    callback(): number | null {
        if (!this.buffer) {
            return null;
        }
        const c = this.buffer[0];
        this.buffer = this.buffer.substr(1);

        return c.charCodeAt(0);
    }

    wait(): Promise<void> {
        return new Promise<void>((res, rej) => {
            this.resolve = res;
            this.reject = rej;
        });
    }
}

class Output {
    private buffer: string;
    private crFlag: boolean;
    private onReceivedGtpResponse: (response: string) => void;

    constructor(onReceivedGtpResponse: (response: string) => void) {
        this.buffer = '';
        this.crFlag = false;
        this.onReceivedGtpResponse = onReceivedGtpResponse;
    }

    callback(char: number): void {
        if (char === 0 || char === 0x0a) {
            if (this.onReceivedGtpResponse)
                this.onReceivedGtpResponse(this.buffer);
            this.buffer = '';
            this.crFlag = false;
            return;
        }
        if (char === 0x0d) {
            this.crFlag = true;
            return;
        }
        if (this.crFlag) {
            this.crFlag = false;
            this.buffer = '';
        }
        this.buffer += String.fromCharCode(char);
    }
}

export const useKataGoEngine = ({
    onEngineReady,
    onEngineError,
    onReceivedGtpResponse,
    onPrint,
    onPrintErr,
    onCommandSubmit,
    model = 'kata1-b6c96-s175395328-d26788732',
    backend = 'auto',
    boardSize = DEFAULT_BOARD_SIZE,
}: {
    onEngineReady?: () => void;
    onEngineError?: () => void;
    onPrint?: (text: string) => void;
    onPrintErr?: (text: string) => void;
    onReceivedGtpResponse: (response: string) => void;
    onSendGtpCommand?: (command: string) => void;
    onCommandSubmit?: (command: string) => void;
    model?: string;
    backend?: string;
    boardSize?: number;
}) => {
    const [input, setInput] = useState<Input>();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    function katagoStatusHandler(status: number) {
        switch (status) {
            case 1:
                if (onEngineReady) onEngineReady();
                break;
            case -1:
                if (onEngineError) onEngineError();
        }
    }

    useEffect(() => {
        let Module: any;
        if (typeof Module === 'undefined') {
            Module = {};
        }
        if (!('preRun' in Module)) {
            Module['preRun'] = [];
        }
        if (!('arguments' in Module)) {
            Module['arguments'] = [];
        }
        Module['print'] = function (text: string) {
            if (onPrint) onPrint(text);
        };
        Module['printErr'] = function (text: string) {
            if (onPrintErr) onPrintErr(text);
        };
        Module['preRun'].push(() => {
            const FS = window.FS;
            const params = new URL((window as any).location).searchParams;
            const modelBaseDir =
                'https://goproblems-public.s3.us-west-2.amazonaws.com/katago';
            const cfgBaseDir = '/api/v2/ai';
            const cfgFile = `gtp_${backend}_${boardSize}.cfg`;
            const cfgPath = `${cfgBaseDir}/${cfgFile}`;
            const modelDir = params.get('model') || `web_model/${model}`;
            const modelPath = `${modelBaseDir}/${modelDir}`;

            // FS.mkdirTree is required to create the directory structure
            // if (baseDir !== '/') {
            FS.mkdirTree(cfgBaseDir);
            // }

            FS.createPreloadedFile(
                cfgBaseDir,
                cfgFile,
                cfgPath,
                true, // read
                false // write
            );
            if (!('arguments' in Module)) {
                Module['arguments'] = [];
            }
            Module['arguments'].push(params.get('subcommand') || 'gtp');
            Module['arguments'].push('-model');
            Module['arguments'].push(modelPath);
            Module['arguments'].push('-config');
            Module['arguments'].push(cfgPath);

            const input = new Input(onCommandSubmit);
            const output = new Output(onReceivedGtpResponse);
            FS.init(
                input.callback.bind(input),
                output.callback.bind(output),
                null
            );
            Module['input'] = input;
            Module['output'] = output;
            setInput(input);
        });

        window.katagoStatusHandler = katagoStatusHandler;
        window.Module = Module;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model, backend, boardSize]);
    return {input};
};
